import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import '../GL/LoanPayment.css';
import { formatDateToDDMMYYYY, getTodayYYYYMMDD } from '../../utils/dateUtils';
import { maskAadhaar, maskPAN } from '../../utils/sensitiveDataUtils';

const TBDLoanPayment = () => {
  const { id, type } = useParams();
  const searchType = type || 'name';
  const [loanDetails, setLoanDetails] = useState(null);
  const [paidDates, setPaidDates] = useState([]);
  const [overdueDetails, setOverdueDetails] = useState([]);
  const [showOverdueDetails, setShowOverdueDetails] = useState(false);
  const [overdueInterestRate, setOverdueInterestRate] = useState(0.5); // Default 0.5% per day
  const [selectedPaymentDate, setSelectedPaymentDate] = useState(getTodayYYYYMMDD());
  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [dailyCollectionAmount, setDailyCollectionAmount] = useState(0);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/tbd-loan-reciever`, {
          params: { type: searchType, value: id },
        });
        setLoanDetails(response.data);
        
        // Calculate daily collection amount
        if (response.data) {
          const dailyAmount = (response.data.loan_amount / response.data.period).toFixed(2);
          setDailyCollectionAmount(parseFloat(dailyAmount));
        }
      } catch (error) {
        console.error('Error fetching loan details:', error);
      }
    };

    fetchLoanDetails();
  }, [id, searchType]);

  useEffect(() => {
    if (loanDetails && loanDetails.account_no) {
      const fetchPaidDates = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
          setPaidDates(response.data);
        } catch (error) {
          console.error('Error fetching paid dates:', error);
        }
      };

      fetchPaidDates();
    }
  }, [loanDetails]);

  // Calculate overdue details for daily collection
  useEffect(() => {
    if (loanDetails && dailyCollectionAmount > 0) {
      const calculateOverdueDetails = () => {
        const currentDate = new Date();
        const loanStartDate = new Date(loanDetails.date);
        const period = loanDetails.period;
        
        // Generate all dates from loan start to current date (or loan end)
        const allDates = [];
        const endDate = new Date(loanStartDate);
        endDate.setDate(endDate.getDate() + period);
        
        for (let d = new Date(loanStartDate); d <= Math.min(currentDate, endDate); d.setDate(d.getDate() + 1)) {
          allDates.push(new Date(d));
        }
        
        const overdueDetails = allDates
          .filter(date => !paidDates.includes(date.toISOString().split('T')[0])) // Filter out paid dates
          .map(date => {
            const daysOverdue = Math.max(0, Math.floor((currentDate - date) / (1000 * 60 * 60 * 24)));
            const overdueInterest = (overdueInterestRate / 100) * daysOverdue * dailyCollectionAmount;
            
            return {
              dueDate: date.toISOString().split('T')[0],
              dailyAmount: dailyCollectionAmount,
              daysOverdue: daysOverdue,
              overdueInterest: overdueInterest.toFixed(2),
            };
          });
        
        setOverdueDetails(overdueDetails);
      };
      
      calculateOverdueDetails();
    }
  }, [loanDetails, paidDates, overdueInterestRate, dailyCollectionAmount]);
  

  // Handle overdue payment submission
  const handleOverduePayment = async (detail) => {
    try {
      await axios.post(`${API_BASE_URL}/tbd-overdue-payment`, {
        account_no: loanDetails.account_no,
        debtor_name: loanDetails.loanee_name,
        overdue_paid: detail.overdueInterest,
        payment_date: detail.dueDate,
        daily_amount: detail.dailyAmount,
        days_overdue: detail.daysOverdue,
      });
      alert('Overdue Payment Successful!');
      // Refresh paid dates
      const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
      setPaidDates(response.data);
    } catch (error) {
      console.error('Error processing overdue payment:', error);
      alert('Overdue Payment failed. Please try again.');
    }
  };

  // Handle daily payment submission
  const handlePayment = async () => {
    if (!selectedPaymentDate || !paymentMode) {
      alert('Please select a payment date and payment mode.');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/tbd-daily-payment`, {
        account_no: loanDetails.account_no,
        payment_date: selectedPaymentDate,
        payment_mode: paymentMode,
        transaction_id: transactionId || null,
        daily_amount: dailyCollectionAmount,
        loanee_name: loanDetails.loanee_name,
      });
      alert('Daily Payment Successful!');
      setIsPaymentSuccessful(true);
      setSelectedPaymentDate(getTodayYYYYMMDD());
      setPaymentMode('');
      setTransactionId('');
      
      // Refresh paid dates
      const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
      setPaidDates(response.data);
    } catch (error) {
      if (error.response && error.response.data.error === 'Already Paid') {
        alert('Payment for this date is already made');
      } else {
        console.error('Error processing payment:', error);
        alert('Payment failed. Please try again.');
      }
    }
  };

  // Generate and print payment slip
  const generateSlip = () => {
    const dailyAmount = dailyCollectionAmount;

    const slipContent = `
  
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
    <div class="details">
    <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    <div class="line"><strong>Daily Collection Amount Rs:</strong> ₹${dailyAmount.toFixed(2)}</div>
    <div class="line"><strong>Payment Date:</strong> ${formatDateToDDMMYYYY(selectedPaymentDate)}</div>
   </div>
    <table class="table">
        <tr>
          <td>DAILY COLLECTION PAID</td>
          <td class="right">${dailyAmount.toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${dailyAmount.toFixed(2)}
      </div>
    <div class="footer">
      Cashier / Mg. Partner                      
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
   <div class="details">
     
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
      <div class="line"><strong>Daily Collection Amount Rs:</strong> ₹${dailyAmount.toFixed(2)}</div>
      <div class="line"><strong>Payment Date:</strong> ${formatDateToDDMMYYYY(selectedPaymentDate)}</div>
    </div>
    <table class="table">
      <tr>
          <td>TBD Interest A/c</td>
          <td class="right">${dailyAmount.toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${dailyAmount.toFixed(2)}
      </div>
    </table>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;
  const printWindow = window.open('', '', 'height=800,width=1200');
  printWindow.document.write('<html><head><title>Interest Slip</title></head><body>');
  printWindow.document.write(slipContent);
  printWindow.document.write('</body></html>');
  printWindow.document.close();
  printWindow.print();
};

  // Toggle overdue details table on Ctrl + B key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'b') {
        setShowOverdueDetails((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!loanDetails) {
    return <h2>Loading loan details...</h2>;
  }
  return (
    <div className="loan-payment-container">
      <div className="loan-payment-header">TBD Daily Collection Payment</div>
      {/* Loan Details */}
      <div className="loan-details">
        <p><strong>Account No:</strong> {loanDetails.account_no}</p>
        <p><strong>Loanee Name:</strong> {loanDetails.loanee_name}</p>
        <p><strong>Date:</strong> {formatDateToDDMMYYYY(loanDetails.date)}</p>
        <p><strong>Aadhaar Debtor:</strong> {maskAadhaar(loanDetails.aadhaar_debtor)}</p>
        <p><strong>PAN Card:</strong> {maskPAN(loanDetails.pan_card)}</p>
        <p><strong>Address Debtor:</strong> {loanDetails.address_debtor}</p>
        <p><strong>Guarantor Name:</strong> {loanDetails.guarantor_name}</p>
        <p><strong>Loan Amount:</strong> ₹{loanDetails.loan_amount}</p>
        <p><strong>Period:</strong> {loanDetails.period} days</p>
        <p><strong>Daily Collection Amount:</strong> ₹{dailyCollectionAmount.toFixed(2)}</p>
        <p><strong>Collector Name:</strong> {loanDetails.collector_name || 'Not specified'}</p>
      </div>
      {/* Payment History */}
      <div className="payment-history">
        <h3>Payment History</h3>
        <p><strong>Total Paid Days:</strong> {paidDates.length} out of {loanDetails.period} days</p>
        <p><strong>Remaining Days:</strong> {loanDetails.period - paidDates.length} days</p>
        <p><strong>Total Amount Paid:</strong> ₹{(paidDates.length * dailyCollectionAmount).toFixed(2)}</p>
        <p><strong>Remaining Amount:</strong> ₹{((loanDetails.period - paidDates.length) * dailyCollectionAmount).toFixed(2)}</p>
      </div>
      {/* Overdue Details */}
      {showOverdueDetails && (
        <div className="overdue-details">
          <h3>Overdue Details (Press Ctrl+B to toggle)</h3>
          <div>
            <label>Overdue Interest Rate (% per day):</label>
            <input
              type="number"
              step="0.1"
              value={overdueInterestRate}
              onChange={(e) => setOverdueInterestRate(e.target.value)}
            />
          </div>
          <table>
            <thead>
              <tr>
                <th>Due Date</th>
                <th>Daily Amount</th>
                <th>Days Overdue</th>
                <th>Overdue Interest</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {overdueDetails.map((detail, index) => (
                <tr key={index}>
                  <td>{formatDateToDDMMYYYY(detail.dueDate)}</td>
                  <td>₹{parseFloat(detail.dailyAmount).toFixed(2)}</td>
                  <td>{detail.daysOverdue}</td>
                  <td>₹{detail.overdueInterest}</td>
                  <td>
                    <button onClick={() => handleOverduePayment(detail)}>Pay Overdue</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Payment Form */}
      <div className="payment-form">
        <h3>Make Daily Payment</h3>
        <div>
          <label>Payment Date:</label>
          <input
            type="date"
            value={selectedPaymentDate}
            onChange={(e) => setSelectedPaymentDate(e.target.value)}
            max={getTodayYYYYMMDD()}
          />
        </div>
        <div>
          <label>Daily Collection Amount:</label>
          <input
            type="text"
            value={`₹${dailyCollectionAmount.toFixed(2)}`}
            readOnly
            style={{backgroundColor: '#f0f0f0'}}
          />
        </div>
        <div>
  <label>Payment Mode:</label>
  <select
    value={paymentMode}
    onChange={(e) => setPaymentMode(e.target.value)}
  >
    <option value="Cash">Cash</option>
    <option value="UPI">UPI</option>
    <option value="Cheque">Cheque</option>
  </select>
</div>
<div>
          <label>Transaction ID:</label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Transaction ID (optional)"
          />
        </div>
        <button onClick={handlePayment}>Submit Daily Payment</button>
      </div>

      {/* Slip Button */}
      {isPaymentSuccessful && (
        <button onClick={generateSlip} className="slip-button">Generate Slip</button>
      )}
    </div>
  );
};

export default TBDLoanPayment;
